/**
 * Module dependencies.
 */

import React, { useState } from 'react';
import { Modal, Select, Input, Form, Space } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { Button } from 'app/components/atoms/button/button';
import { useDataExport } from 'app/hooks/use-data-export';
import { useExportsCount } from 'app/components/providers/exports-count-provider';

/**
 * `ExportButtonProps` type.
 */

export interface ExportButtonProps {
  resource: string;
  queryParams?: Record<string, any>;
  buttonText?: string;
  onExportSuccess?: (exportId: string) => void;
}

/**
 * Export `ExportButton` component.
 */

export function ExportButton({
  resource,
  queryParams,
  buttonText = 'Export',
  onExportSuccess
}: ExportButtonProps): JSX.Element {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const { refetch: refetchExportsCount } = useExportsCount();

  const { exportData, isExporting } = useDataExport({
    resource,
    queryParams,
    onSuccess: exportId => {
      setIsModalVisible(false);
      form.resetFields();
      onExportSuccess?.(exportId);
      refetchExportsCount();
    }
  });

  const handleExport = async () => {
    try {
      const values = await form.validateFields();
      await exportData(values.format, values.filename);
    } catch (error) {
      console.error('Validation error:', error);
    }
  };

  return (
    <>
      <Button icon={<DownloadOutlined />} onClick={() => setIsModalVisible(true)} size={'large'}>
        {buttonText}
      </Button>

      <Modal
        cancelText={'Cancel'}
        confirmLoading={isExporting}
        okText={'Export'}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleExport}
        open={isModalVisible}
        title={'Export Data'}
      >
        <Form form={form} initialValues={{ format: 'csv' }} layout={'vertical'}>
          <Form.Item label={'Format'} name={'format'} rules={[{ required: true, message: 'Please select a format' }]}>
            <Select placeholder={'Select format'}>
              <Select.Option value={'json'}>{'JSON'}</Select.Option>
              <Select.Option value={'csv'}>{'CSV'}</Select.Option>
              <Select.Option value={'xls'}>{'Excel (XLS)'}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={'Filename (optional)'}
            name={'filename'}
            tooltip={'Leave empty for auto-generated filename'}
          >
            <Input placeholder={'my_export'} />
          </Form.Item>

          <Space direction={'vertical'} style={{ width: '100%' }}>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {'The export will be queued and processed in the background. You can download it from the'}{' '}
              <strong>{'Data Exports'}</strong> {'panel (download icon in the top-right corner) once completed.'}
            </div>
          </Space>
        </Form>
      </Modal>
    </>
  );
}
